import { Context } from '@nuxt/types'
import { Inject } from '@nuxt/types/app'
import Vue from 'vue'
import * as Sentry from '@sentry/vue'

declare module 'vue/types/vue' {
  interface Vue {
    $sentry: typeof Sentry
  }
}

declare module '@nuxt/types' {
  interface NuxtAppOptions {
    $sentry: typeof Sentry
  }
  interface Context {
    $sentry: typeof Sentry
  }
}

declare module 'vuex/types/index' {
  interface Store<S> {
    $sentry: typeof Sentry
  }
}

export default ({ app, $config }: Context, inject: Inject): void => {
  // Skip initialization for on-prem deployments
  if ($config.onPrem) {
    const mockSentry = {
      captureException: () => {},
      captureMessage: () => {},
      setUser: () => {},
      withScope: (callback: (scope: unknown) => void) => callback({}),
      addBreadcrumb: () => {},
      setTag: () => {},
      setExtra: () => {},
      setContext: () => {}
    }
    inject('sentry', mockSentry)
    return
  }

  // Build trace propagation targets for distributed tracing
  const tracePropagationTargets: (string | RegExp)[] = ['localhost', /^\//]
  const domains = [$config.domain, 'asgard-main.default'].filter(Boolean)

  domains.forEach((domain) => {
    const escapedDomain = domain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    tracePropagationTargets.push(
      new RegExp(`^(https?:\\/\\/)?([^\\/]+\\.)?${escapedDomain}(\\:8000)?(\\/[^\\s]*)?$`)
    )
  })

  Sentry.init({
    Vue,
    dsn: $config.sentryDsn,
    environment: $config.bifrostEnv || 'production',

    integrations: [
      Sentry.browserTracingIntegration({
        router: app.router
      }),
      Sentry.vueIntegration({
        Vue,
        tracingOptions: {
          trackComponents: true,
          hooks: ['activate', 'create', 'mount', 'update', 'destroy']
        }
      }),
      Sentry.extraErrorDataIntegration(),
      Sentry.reportingObserverIntegration({ types: ['crash'] })
    ],

    // Performance Monitoring
    tracesSampleRate: $config.sentryTracingSampleRate || 0.1,

    // Distributed tracing targets
    tracePropagationTargets,

    beforeSend(event) {
      // Don't send events in development (unless ENABLE_SENTRY_DEV is set)
      if ($config.nodeEnv === 'development' && !$config.enableSentryDev) {
        console.log('[Sentry] Event captured (not sent in dev):', event)
        return null
      }
      return event
    }
  })

  inject('sentry', Sentry)
}
